import { Bert } from 'meteor/themeteorchef:bert';
import methodPromise from '../../modules/method-promise-wrapper';
import LatLon from '../../modules/GeoHelper';

/**
 * Returns a number representing the distance traveled by the RPA in meters.
 *
 * @param {object} waypointsCollectionFeature - GeoJSON Feature Collection with the waypoints.
 * @returns {number} The distance traveled by the RPA.
 */
const getDistanceTraveled = waypointsCollectionFeature => {
  const landingIndex = waypointsCollectionFeature.features.length - 1;
  return waypointsCollectionFeature.features[landingIndex].properties.distanceTraveled;
};

/**
 * Returns a number representing the flight time for that speed in minutes.
 *
 * @param {number} distanceTraveled - The distrance traveled by the RPA in metres.
 * @param {number} flightSpeed - The speed the RPA should be travelling at.
 * @returns {number} The flight time for that distance at that speed in minutes.
 */
const getFlightTime = (distanceTraveled, flightSpeed) => distanceTraveled / flightSpeed / 60;

/**
 * Updates distance and flight time in database.
 *
 * @param {string} missionId - A string representing the id of the mission to update.
 * @param {object} waypointList - GeoJSON Feature Collection with the waypoints. This list has the
 * distance travelled for each waypoint.
 * @param {number} flightSpeed - The speed of the RPA.
 */
const updateDistanceTraveled = (missionId, waypointList, flightSpeed) => {
  const flightDistance = getDistanceTraveled(waypointList);
  const flightTime = getFlightTime(flightDistance, flightSpeed);

  try {
    methodPromise(
      'missions.setDistanceTraveledAndFlightTime',
      missionId,
      flightDistance,
      flightTime
    );
  } catch (error) {
    Bert.alert(error.reason, 'danger');
  }
};

/**
 * Returns the area for the polygon in hectares.
 *
 * @param {object} missionGeometry - GeoJSON Feature Collection with the waypoints.
 * @returns {number} The area of the polygon in hectares.
 */
const getMissionArea = missionGeometry => {
  if (!missionGeometry) return 0;
  if (missionGeometry.geometry.type === 'LineString') return 0;
  const geometryInLatLons = LatLon.prototype.convertGeoJSONToPoints(missionGeometry);
  const missionArea = LatLon.prototype.calculateSurface(geometryInLatLons);
  return missionArea * 100;
};

/**
 * Updates the area data in the mission database.
 *
 * @param {string} missionId - A string representing the id of the mission to update.
 * @param {object} missionGeometry - GeoJSON Feature Collection with the waypoints.
 */
const updateMissionArea = (missionId, missionGeometry) => {
  const newArea = getMissionArea(missionGeometry);
  try {
    methodPromise('missions.setMissionArea', missionId, newArea);
  } catch (error) {
    Bert.alert(error.reason, 'danger');
  }
};

/**
 * Return a string with the Picture Width in metres.
 *
 * @param {number} cameraSensorWidth - The width of the sensor in milimeters.
 * @param {number} focalLength - The focal length of the camera in milimeters.
 * @param {number} missionHeight - The height above ground for the mission in meters.
 * @returns {number} Returns the image width in meters.
 */
const getPictureWidth = (cameraSensorWidth, focalLength, missionHeight) => {
  const matrixWidthInMeters = cameraSensorWidth / 1000;
  const focalInMeters = focalLength / 1000;
  return (matrixWidthInMeters * missionHeight) / focalInMeters;
};

/**
 * Return a string with the Picture Height in metres.
 *
 * @param {number} cameraSensorHeight - The height of the sensor in milimeters.
 * @param {number} focalLength - The focal length of the camera in milimeters.
 * @param {number} missionHeight - The height above ground for the mission in meters.
 * @returns {number} Returns the image height in meters.
 */
const getPictureHeight = (cameraSensorHeight, focalLength, missionHeight) => {
  const matrixHeightInMeters = cameraSensorHeight / 1000;
  const focalInMeters = focalLength / 1000;
  return (matrixHeightInMeters * missionHeight) / focalInMeters;
};

/**
 * Returns the spatial resolution in cm/pix, using the flight height of the UAV,
   focal and sensor width.
 *
 * @param {number} cameraPixelWidth - The width of camera image in pixels.
 * @param {number} cameraSensorWidth - The width of the sensor in milimeters.
 * @param {number} focalLength - The focal length of the camera in milimeters.
 * @param {number} missionHeight - The height above ground for the mission in meters.
 * @returns {number} Returns the final image resolution in cm/px.
 */
const getGroundSampleResolution = (
  cameraPixelWidth,
  cameraSensorWidth,
  focalLength,
  missionHeight
) => {
  const pictureWithInCentiMeters =
    100 * getPictureWidth(cameraSensorWidth, focalLength, missionHeight);
  return pictureWithInCentiMeters / cameraPixelWidth;
};

/**
 * Returns the distace meters between photos transversally.
 *
 * @param {number} cameraSensorWidth - The width of the sensor in milimeters.
 * @param {number} focalLength - The focal length of the camera in milimeters.
 * @param {number} missionHeight - The height above ground for the mission in meters.
 * @param {number} sidelap - The sidelap for the picture grid in percentage.
 * @returns {number} Returns distance between photo centers side by side, on the transversal
 * direction.
 */
const getPicturesSideDistance = (cameraSensorWidth, focalLength, missionHeight, sidelap) => {
  const sidelapFactor = sidelap / 100;
  const pictureWidth = getPictureWidth(cameraSensorWidth, focalLength, missionHeight);
  return pictureWidth - pictureWidth * sidelapFactor;
};

/**
 * Returns the distace meters between photos in the movement direction.
 *
 * @param {number} cameraSensorHeight - The height of the sensor in milimeters.
 * @param {number} focalLength - The focal length of the camera in milimeters.
 * @param {number} missionHeight - The height above ground for the mission in meters.
 * @param {number} overlap - The overlap for the picture grid in percentage.
 * @returns {number} Returns distance between photo centers
 * on the same line, on the movement direction.
 */
const getPictureFrontDistance = (cameraSensorHeight, focalLength, missionHeight, overlap) => {
  const overlapFactor = overlap / 100;
  const pictureHeight = getPictureHeight(cameraSensorHeight, focalLength, missionHeight);
  return pictureHeight - pictureHeight * overlapFactor;
};

/**
 * Returns the time between each picture shoot in seconds.
 *
 * @param {number} cameraSensorHeight - The height of the sensor in milimeters.
 * @param {number} focalLength - The focal length of the camera in milimeters.
 * @param {number} missionHeight - The height above ground for the mission in meters.
 * @param {number} missionSpeed - The speed of the mission in meters per second.
 * @param {number} overlap - The overlap for the picture grid in percentage.
 * @returns {number} Return the number of seconds between each picture.
 */
const getPictureShootTime = (
  cameraSensorHeight,
  focalLength,
  missionHeight,
  missionSpeed,
  overlap
) => {
  const distanceBetweenPhotoCenters = getPictureFrontDistance(
    cameraSensorHeight,
    focalLength,
    missionHeight,
    overlap
  );
  return distanceBetweenPhotoCenters / missionSpeed;
};

export {
  getDistanceTraveled,
  getFlightTime,
  updateDistanceTraveled,
  getMissionArea,
  updateMissionArea,
  getPictureWidth,
  getPictureHeight,
  getGroundSampleResolution,
  getPicturesSideDistance,
  getPictureFrontDistance,
  getPictureShootTime
};
